import axios from "axios";
import { toast } from "react-toastify";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.yuvachangemakers.com";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Cookie helper functions for token storage
const setCookie = (name, value, minutes) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + minutes * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const removeCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Token cache constants
const TOKEN_COOKIE_NAME = "ycm_access_token";
const TOKEN_EXPIRY_COOKIE_NAME = "ycm_token_expiry";
const TOKEN_EMAIL_COOKIE_NAME = "ycm_token_email"; // Store which email the token belongs to

// Token cache to store access token for 30 minutes (persisted in cookies)
const tokenCache = {
  token: null,
  expiresAt: null,
  email: null, // Track which email this token belongs to
};

// Initialize token cache from cookies on page load
const initTokenCache = () => {
  const storedToken = getCookie(TOKEN_COOKIE_NAME);
  const storedExpiry = getCookie(TOKEN_EXPIRY_COOKIE_NAME);
  const storedEmail = getCookie(TOKEN_EMAIL_COOKIE_NAME);

  if (storedToken && storedExpiry && storedEmail) {
    const expiryTime = parseInt(storedExpiry, 10);
    if (expiryTime > Date.now()) {
      tokenCache.token = storedToken;
      tokenCache.expiresAt = expiryTime;
      tokenCache.email = storedEmail;
      console.log("Token restored from cookies for email:", storedEmail);
    } else {
      // Token expired, clean up cookies
      removeCookie(TOKEN_COOKIE_NAME);
      removeCookie(TOKEN_EXPIRY_COOKIE_NAME);
      removeCookie(TOKEN_EMAIL_COOKIE_NAME);
      console.log("Expired token removed from cookies");
    }
  }
};

// Initialize cache on module load
initTokenCache();

/**
 * API 1: Click Next - Save page data when user clicks Next
 * @param {number} pageNumber - Current page number (1 or 2)
 * @param {Object} formData - Form data for the current page
 * @returns {Promise} - API response
 */
export const clickNext = async (pageNumber, formData) => {
  // Don't call API if email is not available
  const email = formData?.email;
  if (!email || email.trim() === "") {
    console.log("Skipping clickNext API - email not available");
    toast.error("Invalid email, Rfresh the page and try again.");
    return null;
  }

  let payload;

  if (pageNumber === 1) {
    // Page 1 payload
    payload = {
      page_number: 1,
      name: formData?.name,
      age: parseInt(formData?.age),
      gender: formData?.gender,
      district: formData?.district,
      assembly_constituency: formData?.assemblyConstituency,
      panchayat: formData?.panchayat,
      village: formData?.village,
      phone: parseInt(formData?.phone),
      email: formData?.email,
      himachal_domicile: formData?.himachaliDomicile === "Yes",
      category: formData?.category,
      is_bpl: formData?.bpl === "Yes",
      marriatal_status: formData?.maritalStatus,
      referred_by_code: formData.referralCode || null,
    };
  } else if (pageNumber === 2) {
    // Page 2 payload
    payload = {
      page_number: 2,
      email: formData?.email,
      educational_qualification: formData?.educationalQualification,
      profession: formData?.profession,
      worked_with_youth_organization: formData?.youthOrganization,
      selected_for_youth_program: formData?.leadershipProgram,
      mother_name: formData?.motherName,
      mother_profession: formData?.motherProfession,
      father_name: formData?.fatherName,
      father_profession: formData.fatherProfession,
      no_of_siblings: formData.siblings,
      referred_by_code: formData.referralCode || null,
    };
  } else if (pageNumber === 3) {
    // Page 3 payload - called when video is uploaded
    payload = {
      page_number: 3,
      email: formData?.email,
      mountain_or_beach: formData?.mountainOrBeach || null,
      favourite_podcaster:
        formData?.favoritePodcaster === "Other"
          ? formData?.favoritePodcasterOther || null
          : formData?.favoritePodcaster || null,
      binod_or_kirish: formData?.binodOrKiris || null,
      gemini_gpt_perplexity: formData?.aiPreference || null,
      no_of_noob_friends: formData?.noobFriends || null,
      ai_image_prompt: formData?.aiImagePrompt || null,
      ghazal_or_hip_hop: formData?.ghazalOrHipHop || null,
      thoughts_on_changemakers: formData?.changemakersWord || null,
      solve_issue_in_himachal: formData?.issueToSolve || null,
      upload_video: formData?.uploadedVideoFilename || null,
      referred_by_code: formData?.referralCode || null,
    };
  }

  console.log("Clicking Next - Page", pageNumber, payload);

  try {
    // Step 1: Generate access token with retry logic (3 attempts)
    const accessToken = await generateAccessToken(email);

    // Step 2: Use the token to submit the form data
    const response = await apiClient.post("/form/click-next", payload, {
      headers: {
        Authorization: accessToken,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error in click-next API:", error);

    throw error;
  }
};

/**
 * API 2: Upload Video
 * @param {File} videoFile - The video file to upload
 * @returns {Promise} - API response with video URL
 */
export const uploadVideo = async (videoFile, email) => {
  if (!videoFile) {
    return {
      success: true,
      message: "No video to upload",
      data: null,
    };
  }

  console.log("Uploading video:", {
    fileName: videoFile.name,
    fileSize: videoFile.size,
    fileType: videoFile.type,
  });

  try {
    // Step 1: Generate access token with retry logic (3 attempts)
    const accessToken = await generateAccessToken(email);

    // Step 2: Upload video with token
    const formDataPayload = new FormData();
    formDataPayload.append("file", videoFile);
    formDataPayload.append("gallery_file_type", "VIDEO");

    const response = await apiClient.post(`/upload/${email}`, formDataPayload, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: accessToken,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading video:", error);
    throw error;
  }
};

/**
 * API 3: Submit Complete Form
 * @param {Object} formData - The complete form data
 * @param {string} videoUrl - URL of uploaded video (if any)
 * @param {string} captchaToken - reCAPTCHA token for server-side verification
 * @returns {Promise} - API response
 */
export const submitForm = async (
  formData,
  videoUrl = null,
  captchaToken = null,
) => {
  const payload = {
    // Page 1 data
    name: formData.name,
    age: parseInt(formData.age),
    gender: formData.gender,
    district: formData.district,
    assembly_constituency: formData.assemblyConstituency,
    panchayat: formData.panchayat,
    village: formData.village,
    phone: parseInt(formData.phone),
    email: formData.email,
    himachal_domicile: formData.himachaliDomicile === "Yes",
    category: formData.category,
    is_bpl: formData.bpl === "Yes",
    marriatal_status: formData.maritalStatus,

    // Page 2 data
    educational_qualification: formData.educationalQualification,
    profession: formData.profession,
    worked_with_youth_organization: formData.youthOrganization,
    selected_for_youth_program: formData.leadershipProgram,
    mother_name: formData.motherName,
    mother_profession: formData.motherProfession,
    father_name: formData.fatherName,
    father_profession: formData.fatherProfession,
    no_of_siblings: formData.siblings,

    // Page 3 data
    mountain_or_beach: formData.mountainOrBeach || null,
    favourite_podcaster:
      formData.favoritePodcaster === "Other"
        ? formData.favoritePodcasterOther || null
        : formData.favoritePodcaster || null,
    binod_or_kirish: formData.binodOrKiris || null,
    gemini_gpt_perplexity: formData.aiPreference || null,
    no_of_noob_friends: formData.noobFriends || null,
    ai_image_prompt: formData.aiImagePrompt || null,
    ghazal_or_hip_hop: formData.ghazalOrHipHop || null,
    thoughts_on_changemakers: formData.changemakersWord || null,
    solve_issue_in_himachal: formData.issueToSolve || null,

    // Video path from upload API response
    upload_video: videoUrl || null,

    // Referral code if provided (referred by someone)
    referred_by_code: formData.referralCode || null,

    // reCAPTCHA token for server-side verification
    recaptcha_token: captchaToken,
  };

  console.log("Submitting complete form:", payload);
  console.log("favourite_podcaster value:", payload.favourite_podcaster);
  console.log("formData.favoritePodcaster:", formData.favoritePodcaster);
  console.log(
    "formData.favoritePodcasterOther:",
    formData.favoritePodcasterOther,
  );

  try {
    // Step 1: Generate access token with retry logic (3 attempts)
    const accessToken = await generateAccessToken(formData.email);

    // Step 2: Submit complete form with token
    const response = await apiClient.post("/form/submit-form", payload, {
      headers: {
        Authorization: accessToken,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting form:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

/**
 * API 3: Fetch Referral Code with Access Token
 * @param {string} email - User email
 * @param {string} captchaToken - reCAPTCHA token for server-side verification
 * @returns {Promise} - API response
 */
export const fetchRefCode = async (email, captchaToken = null) => {
  try {
    // Step 1: Generate access token with retry logic (3 attempts)
    const accessToken = await generateAccessToken(email);

    // Step 2: Use the token to fetch the referral code
    const response = await apiClient.post(
      "/form/get-my-referral-code",
      {
        email,
        recaptcha_token: captchaToken,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    );

    return response?.data;
  } catch (error) {
    console.error("Error fetching referral code:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const validateRefCode = async (referral_code, email = null) => {
  const endpoint = `/form/beta-alpha-gama-get-code/${referral_code}`;

  try {
    // Prepare request config
    const config = {};

    // If email is provided, generate token and add to headers
    if (email && email.trim() !== "") {
      const accessToken = await generateAccessToken(email);
      config.headers = {
        Authorization: accessToken,
      };
    }

    const response = await apiClient.get(endpoint, config);

    return response?.data;
  } catch (error) {
    console.error(`Validation failed for code: ${referral_code}`);
    console.error("Server Response:", error.response?.data || error.message);

    throw error;
  }
};
export const fetchDataByEmail = async (email, signal = null) => {
  const endpoint = `/form/get-prefilled-data/${email}`;

  try {
    // Step 1: ALWAYS generate fresh token (skipCache = true)
    // This prevents the glitch when user changes email
    const accessToken = await generateAccessToken(email, true); // skipCache = true

    // Step 2: Use the fresh token to fetch prefilled data
    const response = await apiClient.get(endpoint, {
      signal,
      headers: {
        Authorization: accessToken,
      },
    });

    return response?.data;
  } catch (error) {
    // Re-throw abort errors without logging
    if (error.name === "AbortError" || error.name === "CanceledError") {
      throw error;
    }
    console.error(`Failed to fetch data for email: ${email}`);
    console.error("Server Response:", error.response?.data || error.message);

    throw error;
  }
};
/**
 * Generate Access Token with Retry Logic and Smart Caching
 * @param {string} email - User email to generate token for
 * @param {boolean} skipCache - If true, always generate fresh token (for fetchDataByEmail)
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @returns {Promise<string>} - Access token
 */
export const generateAccessToken = async (
  email,
  skipCache = false,
  maxRetries = 3,
) => {
  const now = Date.now();

  // Check if we should use cached token
  // Only use cache if:
  // 1. skipCache is false (not from fetchDataByEmail)
  // 2. Token exists and hasn't expired
  // 3. Token belongs to the SAME email (prevents glitch!)
  if (
    !skipCache &&
    tokenCache.token &&
    tokenCache.expiresAt &&
    now < tokenCache.expiresAt &&
    tokenCache.email === email // IMPORTANT: Check email matches!
  ) {
    console.log("Using cached access token for email:", email);
    return tokenCache.token;
  }

  // Generate fresh token
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(
        `Generating fresh access token for ${email} (attempt ${attempt}/${maxRetries})`,
      );

      const response = await apiClient.post("/auth/generate-access-token", {
        email,
      });

      const token = response?.data?.data?.token;

      if (!token) {
        throw new Error("No token received from server");
      }

      // Always cache the newly generated token
      // This allows other APIs to reuse it
      const expiryTime = now + 1800000; // 30 minutes

      tokenCache.token = token;
      tokenCache.expiresAt = expiryTime;
      tokenCache.email = email; // Store which email this token belongs to

      // Store in cookies for persistence
      setCookie(TOKEN_COOKIE_NAME, token, 30);
      setCookie(TOKEN_EXPIRY_COOKIE_NAME, expiryTime.toString(), 30);
      setCookie(TOKEN_EMAIL_COOKIE_NAME, email, 30);

      if (skipCache) {
        console.log("Fresh token generated and cached for email:", email);
      } else {
        console.log("Token generated and cached for email:", email);
      }

      return token;
    } catch (error) {
      lastError = error;
      console.error(`Token generation attempt ${attempt} failed:`, error);

      // If this isn't the last attempt, wait a bit before retrying
      if (attempt < maxRetries) {
        // Exponential backoff: 500ms, 1000ms, 1500ms
        const delay = attempt * 500;
        console.log(`Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All retry attempts failed
  console.error("All token generation attempts failed:", lastError);
  toast.error(
    "Unable to generate access token. Please refresh your page or try after sometime.",
  );
  throw lastError;
};
